import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, IndianRupee, Send, TrendingUp, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { customerAPI, customerPaymentAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/utils';

export function PaymentsPage() {
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
  const [quickPaymentAmount, setQuickPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'upi'>('cash')

  const { data: paymentSummary, isLoading: paymentSummaryLoading } = useQuery({
    queryKey: ['agency-payment-summary'],
    queryFn: () => customerPaymentAPI.getAgencySummary().then(r => r.data),
  });

  const createQuickPaymentMutation = useMutation({
    mutationFn: (data: any) => customerPaymentAPI.create(data.customer_id, {
      total_bill_amount: data.total_bill_amount,
      paid_amount: data.paid_amount,
      items: [],
      transactions: data.transactions,
    }),
    onSuccess: () => {
      toast.success('Payment recorded successfully')
      setSelectedCustomer(null)
      setQuickPaymentAmount('')
      setPaymentMethod('cash')
      queryClient.invalidateQueries({ queryKey: ['payment-summaries-all'] })
      queryClient.invalidateQueries({ queryKey: ['agency-payment-summary'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to record payment')
    },
  })

  const handleQuickPaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedCustomer) {
      toast.error('No customer selected')
      return
    }

    const amount = parseInt(quickPaymentAmount) || 0
    if (amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (amount > (selectedCustomer.paymentSummary?.pending_amount || 0)) {
      toast.error('Payment amount cannot exceed pending amount')
      return
    }

    createQuickPaymentMutation.mutate({
      customer_id: selectedCustomer.id,
      total_bill_amount: 0,
      paid_amount: amount,
      payment_method: paymentMethod,
      transactions: [
        {
          amount: amount,
          payment_method: paymentMethod,
          notes: 'Quick Payment',
        },
      ],
    })
  }

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: () =>
      customerAPI
        .list({
          limit: 1000,
          offset: 0,
        })
        .then((r) => r.data),
  })

  const { data: paymentSummaries, isLoading: paymentSummariesLoading } = useQuery({
    queryKey: ['payment-summaries-all', customersData?.customers?.map(c => c.id).join(',')],
    queryFn: async () => {
      const summaries: Record<string, any> = {}
      if (customersData?.customers && customersData.customers.length > 0) {
        await Promise.all(
          customersData.customers.map(async (customer) => {
            try {
              const response = await customerPaymentAPI.getSummary(customer.id)
              summaries[customer.id] = response.data
            } catch (error) {
              summaries[customer.id] = null
            }
          })
        )
      }
      return summaries
    },
    enabled: !!customersData?.customers && customersData.customers.length > 0,
  })

  // Sort customers by pending amount (descending) and filter out those with no pending
  const sortedCustomers = useMemo(() => {
    if (!customersData?.customers || !paymentSummaries) return []

    return customersData.customers
      .map((customer) => ({
        ...customer,
        paymentSummary: paymentSummaries[customer.id],
      }))
      .filter((customer) => (customer.paymentSummary?.pending_amount || 0) > 0)
      .sort(
        (a, b) =>
          (b.paymentSummary?.pending_amount || 0) - (a.paymentSummary?.pending_amount || 0)
      )
  }, [customersData?.customers, paymentSummaries])

  const isLoading = paymentSummaryLoading || customersLoading || paymentSummariesLoading

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="View payment summary and customer payment details"
      />

      <div className="space-y-6">
        {/* Payment Summary Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Billed */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-700 font-medium">Total Billed</p>
                <TrendingUp className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-900">
                ₹{(paymentSummary?.total_billed || 0).toFixed(2)}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                {paymentSummary?.bill_count || 0} invoices
              </p>
            </div>

            {/* Total Paid */}
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-green-700 font-medium">Total Paid</p>
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-900">
                ₹{(paymentSummary?.total_paid || 0).toFixed(2)}
              </p>
              <p className="text-xs text-green-600 mt-2">
                {paymentSummary?.payment_count || 0} payments
              </p>
            </div>

            {/* Pending Amount */}
            <div className={`p-4 bg-gradient-to-br ${
              (paymentSummary?.total_pending || 0) > 0
                ? 'from-red-50 to-red-100 border border-red-200'
                : 'from-green-50 to-green-100 border border-green-200'
            } rounded-lg`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm font-medium ${
                  (paymentSummary?.total_pending || 0) > 0
                    ? 'text-red-700'
                    : 'text-green-700'
                }`}>
                  Pending Amount
                </p>
                <AlertCircle className={`w-4 h-4 ${
                  (paymentSummary?.total_pending || 0) > 0
                    ? 'text-red-600'
                    : 'text-green-600'
                }`} />
              </div>
              <p className={`text-2xl font-bold ${
                (paymentSummary?.total_pending || 0) > 0
                  ? 'text-red-900'
                  : 'text-green-900'
              }`}>
                ₹{(paymentSummary?.total_pending || 0).toFixed(2)}
              </p>
              {(paymentSummary?.total_pending || 0) > 0 ? (
                <p className="text-xs text-red-600 mt-2">
                  Outstanding due from customers
                </p>
              ) : (
                <p className="text-xs text-green-600 mt-2">
                  All payments collected
                </p>
              )}
            </div>
          </div>
        )}

        {/* Payment Stats */}
        {!isLoading && paymentSummary && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {(paymentSummary?.recovery_percentage || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-600 mt-1">Recovery Rate</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {paymentSummary?.customer_count || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">Customers</p>
              </div>
            </div>
          </div>
        )}

        {/* Customer Payments Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-saffron" />
              Customer Payment Details
            </h2>
          </div>

          {isLoading ? (
            <div className="space-y-2 p-6">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : sortedCustomers.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-gray-500 p-6">
              <p>No customers yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-700">
                      Customer Name
                    </th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-700">
                      Mobile No
                    </th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-700">
                      Total Bill
                    </th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-700">
                      Total Paid
                    </th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-700">
                      Pending Amount
                    </th>
                    <th className="text-center py-3 px-6 text-xs font-semibold text-gray-700">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedCustomers.map((customer) => {
                    const summary = customer.paymentSummary
                    const pendingAmount = summary?.pending_amount || 0

                    return (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="py-3 px-6 font-medium text-gray-900">
                          {customer.full_name}
                        </td>
                        <td className="py-3 px-6 text-gray-600">
                          {customer.phone}
                        </td>
                        <td className="py-3 px-6 text-right text-gray-900">
                          ₹{(summary?.total_billed || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-6 text-right text-green-600 font-medium">
                          ₹{(summary?.total_paid || 0).toFixed(2)}
                        </td>
                        <td className={cn(
                          'py-3 px-6 text-right font-semibold',
                          pendingAmount > 0 ? 'text-red-600' : 'text-green-600'
                        )}>
                          ₹{pendingAmount.toFixed(2)}
                        </td>
                        <td className="py-3 px-6 text-center">
                          <button
                            onClick={() => setSelectedCustomer(customer)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-white bg-saffron hover:bg-orange-600 rounded-lg transition"
                          >
                            <Send className="w-4 h-4" />
                            Pay
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Payment Modal */}
        {selectedCustomer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900">Quick Payment</h2>
                <button
                  onClick={() => {
                    setSelectedCustomer(null)
                    setQuickPaymentAmount('')
                  }}
                  className="rounded-lg p-1 hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <form onSubmit={handleQuickPaymentSubmit} className="space-y-4 p-6">
                {/* Customer Info */}
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                  <p className="text-sm text-blue-700 font-medium">Customer</p>
                  <p className="text-lg font-semibold text-blue-900 mt-1">
                    {selectedCustomer.full_name}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">{selectedCustomer.phone}</p>
                </div>

                {/* Pending Amount Display */}
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-700 font-medium">Pending Amount</p>
                  <p className="text-2xl font-bold text-red-900 mt-1">
                    ₹{(selectedCustomer.paymentSummary?.pending_amount || 0).toFixed(2)}
                  </p>
                </div>

                {/* Payment Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Amount (₹) *
                  </label>
                  <input
                    type="number"
                    value={quickPaymentAmount}
                    onChange={(e) => setQuickPaymentAmount(e.target.value)}
                    placeholder="0"
                    step="1"
                    min="0"
                    max={selectedCustomer.paymentSummary?.pending_amount || 0}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-2xl font-bold focus:border-saffron focus:outline-none"
                    required
                  />
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method *
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'upi')}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:border-saffron focus:outline-none"
                  >
                    <option value="cash">💵 Cash</option>
                    <option value="upi">📱 UPI</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={createQuickPaymentMutation.isPending}
                    className="flex-1 bg-saffron hover:bg-orange-600"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null)
                      setQuickPaymentAmount('')
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
